// Deep import: the `@civilgrid/utils` barrel re-exports pino and redis, which must not follow this
// into the browser bundle. `src/extract/types` has no imports of its own.
import { MAX_EXTRACT_FORMATS } from '@civilgrid/utils/src/extract/types';
import type { ExtractProjection, ProjectExtractRequest } from '~/api/extract';
import type { ExportEntryPoint } from '~/common/posthogEvents';

export const ExtractFormat = {
  DWG: 'DWG',
  DGN: 'DGN',
  KMZ: 'KMZ',
  SHP: 'SHP',
} as const;

export type TExtractFormat = (typeof ExtractFormat)[keyof typeof ExtractFormat];

export const ExtractFormatLabelMap = {
  DWG: 'AutoCAD DWG',
  DGN: 'Bentley MicroStation DGN',
  KMZ: 'Google Earth KMZ',
  SHP: 'ESRI Shapefile',
} as const satisfies Record<TExtractFormat, string>;

// Hard-coded format options sourced from the shared ExtractFormat enum (DWG, DGN, KMZ, SHP).
// `value` is the format string the extract endpoint expects; `label` is the display name.
export const extractFormats = (Object.values(ExtractFormat) as TExtractFormat[]).map((format) => ({
  value: format,
  label: ExtractFormatLabelMap[format],
}));

export { MAX_EXTRACT_FORMATS };

/**
 * Build the POST /v1/extract/projectExtract payload. Layer IDs are submitted RAW
 * (no ESRI transform) — the backend maps them via LEGACY_ExtractLayerMap.
 */
export function buildProjectExtractRequest(params: {
  projectId: string;
  organizationId: string;
  selectedLayerIds: Iterable<string>;
  formats: TExtractFormat[];
  projectionId: string;
  extractTemplateId?: string | null;
  ccEmails?: string[];
}): ProjectExtractRequest {
  return {
    projectId: params.projectId,
    organizationId: params.organizationId,
    layerIds: Array.from(params.selectedLayerIds),
    // `format` carries the first selection so an API deployment that predates the multi-format
    // endpoint still gets a format it understands; the server unions the two and de-duplicates.
    format: params.formats[0],
    formats: params.formats,
    projectionId: params.projectionId,
    ...(params.ccEmails && { ccEmails: params.ccEmails }),
    ...(params.extractTemplateId !== undefined && { extractTemplateId: params.extractTemplateId }),
  };
}

/**
 * Properties for the `project_export_initiated` event a submitted export fires. Pure so the payload
 * is assertable without rendering the form.
 */
export function buildExportInitiatedProperties(params: {
  projectId: string;
  organizationId: string;
  formats: TExtractFormat[];
  selectedLayerIds: Iterable<string>;
  submissionId?: string;
  extractTemplateId?: string | null;
  extractTemplateName?: string | null;
  projectionId?: string | null;
  projectionName?: string | null;
  ccRecipientCount?: number;
  entryPoint?: ExportEntryPoint;
}) {
  const { formats } = params;

  return {
    project_id: params.projectId,
    organization_id: params.organizationId || undefined,
    // A single format keeps reporting that format, so existing single-format breakdowns keep their
    // meaning; several report 'multi' and are itemised in export_types.
    export_type: formats.length === 1 ? formats[0].toLowerCase() : 'multi',
    export_types: formats.map((format) => format.toLowerCase()),
    export_format_count: formats.length,
    submission_id: params.submissionId,
    extract_template_id: params.extractTemplateId || undefined,
    extract_template_name: params.extractTemplateName || undefined,
    projection_id: params.projectionId || undefined,
    projection_name: params.projectionName || undefined,
    source: 'extract_download',
    selected_layer_ids: Array.from(params.selectedLayerIds),
    cc_recipient_count: params.ccRecipientCount,
    entry_point: params.entryPoint,
  };
}

/**
 * Properties for the `project_export_failed` event a rejected export submission fires. Pure so the
 * payload is assertable without rendering the form.
 */
export function buildExportFailedProperties(params: { projectId: string; organizationId: string; reason: string }) {
  return {
    project_id: params.projectId,
    organization_id: params.organizationId || undefined,
    reason: params.reason,
  };
}

/**
 * Whether the selection can be submitted: at least one format, and no more than the server accepts.
 */
export function isFormatSelectionValid(formats: TExtractFormat[]): boolean {
  return formats.length > 0 && formats.length <= MAX_EXTRACT_FORMATS;
}

/**
 * Human list of the layers an export skipped, or `null` when it skipped none. Names come from the
 * picker's own labels, since a raw layer id means nothing to whoever submitted the export.
 */
export function describeDroppedLayers(
  droppedLayerIds: string[] | undefined,
  labelForLayerId: (layerId: string) => string | undefined,
): string | null {
  if (!droppedLayerIds?.length) return null;

  const names = droppedLayerIds.map((layerId) => labelForLayerId(layerId) ?? layerId);
  if (names.length === 1) return names[0];

  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** How long the submit button stays disabled after an accepted submission. */
export const SUBMISSION_COOLDOWN_MS = 20_000;

/** A submission the client has already sent, kept to hold the button for its cooldown. */
export type RecordedSubmission = { projectId: string; at: number };

export type SubmissionBlock = { reason: 'cooldown'; secondsRemaining: number };

/**
 * Why a resubmit is refused, or `null` when it may proceed. A short window off the last submission,
 * so repeated clicks can't fan one export out into several FME runs without holding up a deliberate
 * re-export.
 */
export function getSubmissionBlock(params: {
  lastSubmission: RecordedSubmission | null;
  projectId: string;
  now: number;
}): SubmissionBlock | null {
  const { lastSubmission, projectId, now } = params;
  // Scoped to one project: an export started elsewhere is a separate FME run and must not hold this
  // form, and the store outlives a move between projects.
  if (!lastSubmission || lastSubmission.projectId !== projectId) return null;

  const elapsed = now - lastSubmission.at;

  if (elapsed >= SUBMISSION_COOLDOWN_MS) return null;

  return { reason: 'cooldown', secondsRemaining: Math.ceil((SUBMISSION_COOLDOWN_MS - elapsed) / 1000) };
}

/**
 * When this project's cooldown lapses, or `null` when nothing is blocked. Drives the countdown, and
 * takes `projectId` for the same reason `getSubmissionBlock` does — otherwise moving to another
 * project leaves a timer running for a block that no longer applies.
 */
export function getSubmissionBlockExpiry(lastSubmission: RecordedSubmission | null, projectId: string): number | null {
  if (!lastSubmission || lastSubmission.projectId !== projectId) return null;

  return lastSubmission.at + SUBMISSION_COOLDOWN_MS;
}

/**
 * Add or remove one format from the selection. Appends rather than sorting, so the submitted order
 * is the order the user picked them in.
 */
export function toggleFormat(formats: TExtractFormat[], format: TExtractFormat, checked: boolean): TExtractFormat[] {
  return checked ? [...formats, format] : formats.filter((selected) => selected !== format);
}

/**
 * Whether the projection picker applies to the selected formats. KMZ exports ignore it (Google
 * Earth is always WGS84), so neither an all-KMZ selection nor an empty one has anything to project.
 * The picker stays visible either way and is disabled instead, so its tooltip can say why.
 */
export function isProjectionPickerEnabled(formats: TExtractFormat[]): boolean {
  return formats.some((format) => format !== ExtractFormat.KMZ);
}

/**
 * Whether the Projection field should render at all. In the multi-format flow it's hidden rather
 * than disabled when no selected format needs it, since a blank disabled dropdown reads as broken.
 * The single-format flow keeps the disabled-but-visible behavior above.
 */
export function isProjectionFieldVisible(multiFormatEnabled: boolean, formats: TExtractFormat[]): boolean {
  return multiFormatEnabled ? isProjectionPickerEnabled(formats) : true;
}

/**
 * Whether the Extract Template field should render. In the multi-format flow it's hidden when the
 * organization has no assigned templates, since the only option left is "CivilGrid Default".
 */
export function isTemplateFieldVisible(multiFormatEnabled: boolean, templateCount: number): boolean {
  return multiFormatEnabled ? templateCount > 0 : true;
}

/**
 * The multi-format flag resolves asynchronously, so the form can't default to an empty format
 * selection in its initial state — it mounts with today's single-format default before the flag
 * is known. This clears that untouched default the first time the flag resolves true, without
 * touching a selection the user already made in the window before it resolved.
 *
 * Once `export-multi-format-enabled` (`EXPORT_MULTI_FORMAT_FLAG`) is fully rolled out and the
 * single-format branch is removed, `formats` can default to `[]` directly and this function
 * goes away.
 */
export function resetFormatsWhenMultiFormatEnabled(
  multiFormatEnabled: boolean,
  formats: TExtractFormat[],
): TExtractFormat[] {
  if (!multiFormatEnabled) return formats;
  const isUntouchedDefault = formats.length === 1 && formats[0] === ExtractFormat.DWG;
  return isUntouchedDefault ? [] : formats;
}

/**
 * What the projection Select should show. Blank while the picker is disabled, so a greyed-out
 * projection can't read as the one being applied — the stored id survives, so re-enabling restores
 * the user's choice rather than making them pick again.
 */
export function projectionSelectValue(formats: TExtractFormat[], projectionId: string | undefined | null): string {
  return isProjectionPickerEnabled(formats) ? (projectionId ?? '') : '';
}

/**
 * Display labels for the selected formats, in selection order. Feeds the manual download request,
 * whose `fileFormat` is interpolated into a Slack message as free-form text.
 */
export function formatSelectionLabel(formats: TExtractFormat[]): string {
  return formats.map((format) => ExtractFormatLabelMap[format]).join(', ');
}

/**
 * Secondary text for a projection option in the extract form's ComboBox, which displays it beside
 * the label and also matches search against it. Leaves out the EPSG code and FME name when the label
 * already contains them, and an FME name that only restates the EPSG code.
 */
export function getProjectionDescription({ label, epsgCode, fmeFormat }: ExtractProjection): string {
  const labelNumbers: string[] = label.match(/\d+/g) ?? [];
  const showCode = !!epsgCode && !labelNumbers.includes(epsgCode);
  const fmeFormatLower = fmeFormat?.toLowerCase() ?? '';
  const showFmeFormat =
    !!fmeFormatLower && !label.toLowerCase().includes(fmeFormatLower) && fmeFormatLower !== `epsg:${epsgCode}`;
  return [showCode && epsgCode, showFmeFormat && fmeFormat].filter(Boolean).join(' ');
}

/** Option value standing in for the built-in template, since Radix Select reserves the empty string. */
export const DEFAULT_TEMPLATE_OPTION = '__civilgrid_default__';

export function templatePickerOptions(
  templates: { name: string; extractTemplateId: string }[],
): { label: string; value: string }[] {
  return [
    { label: 'CivilGrid Default', value: DEFAULT_TEMPLATE_OPTION },
    ...templates.map((template) => ({ label: template.name, value: template.extractTemplateId })),
  ];
}

export function templateIdFromOption(value: string | null): string | null {
  return value === null || value === DEFAULT_TEMPLATE_OPTION ? null : value;
}

// EPSG code (Web Mercator) used as the default projection for the extract form.
export const DEFAULT_PROJECTION_EPSG_CODE = '3857';

/**
 * Pick the default projection for the extract form: Web Mercator (EPSG:3857) when it
 * is available, rather than an arbitrary first row (which risks submitting an extract
 * in the wrong spatial reference). Returns `undefined` when 3857 is absent so the user
 * must choose explicitly. The client projection shape only exposes `epsgCode`.
 */
export function selectDefaultProjectionId(projections: ExtractProjection[]): string | undefined {
  return projections.find((p) => p.epsgCode === DEFAULT_PROJECTION_EPSG_CODE)?.id;
}

/**
 * Choose the projection to pre-select on load: prefer the server's best-fit projection
 * (nearest intersecting coverage zone) when present, otherwise fall back to the Web Mercator
 * default. Returns `undefined` when neither is available, so the user must choose explicitly.
 */
export function chooseInitialProjectionId(
  best: ExtractProjection | null,
  projections: ExtractProjection[],
): string | undefined {
  return best?.id ?? selectDefaultProjectionId(projections);
}

// Maximum project size (acres) eligible for a direct download — mirrors the legacy tab.
export const MAX_DIRECT_DOWNLOAD_ACRES = 2500;

/** Whole acres, or up to two decimals under 10 acres. */
export function formatAreaAcres(acres: number): string {
  return `${acres.toLocaleString('en-US', { maximumFractionDigits: acres < 10 ? 2 : 0 })} Ac`;
}

/**
 * Whether direct download must be blocked (matching the legacy `new-download`
 * `returnIfDownloadDisabled`): oversized project, nothing selected, no projection,
 * or any selected layer is partner-restricted. When disabled the user falls back to
 * a manual download request.
 */
export function isExtractDownloadDisabled(params: {
  downloadArea: number;
  selectedLayerCount: number;
  projectionId?: string | null;
  hasRestrictedLayer: boolean;
  formats: TExtractFormat[];
  limitsUnknown: boolean;
}): boolean {
  return (
    params.downloadArea > MAX_DIRECT_DOWNLOAD_ACRES ||
    params.selectedLayerCount === 0 ||
    !isFormatSelectionValid(params.formats) ||
    !params.projectionId ||
    params.hasRestrictedLayer ||
    params.limitsUnknown
  );
}

/**
 * Whether to offer the manual download-request fallback. It mirrors the direct-download
 * disabled gate but additionally requires a chosen projection and a valid format selection:
 * without those guards the fallback would render while `projectionId` is unset, or while the
 * user has simply not picked a format yet, and submit a request with an empty label. It also
 * waits while the limits are checked, since the project may turn out to need no request.
 */
export function shouldShowManualRequestFallback(params: {
  isDownloadDisabled: boolean;
  selectedLayerCount: number;
  projectionId?: string | null;
  formats: TExtractFormat[];
  limitsChecking: boolean;
}): boolean {
  return (
    params.isDownloadDisabled &&
    !params.limitsChecking &&
    params.selectedLayerCount > 0 &&
    !!params.projectionId &&
    isFormatSelectionValid(params.formats)
  );
}
